import React, { useState, useEffect, useMemo } from "react";
import swal from "sweetalert";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext"; // 🔹 For currentCity auto-select

const RegisterInterestModal = ({ show, onClose }) => {
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});

  // OTP States
  const [step, setStep] = useState("register");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);

  const { publicAuth } = useApi();
  const { currentCity } = useAuth();
  const isLoggedIn = localStorage.getItem("user");

  const states = useMemo(() => {
    const set = new Set();
    cities.forEach((c) => {
      if (c?.state_name) set.add(c.state_name);
    });
    return Array.from(set).sort();
  }, [cities]);

  const filteredCities = useMemo(() => {
    if (!selectedState) return [];
    return cities.filter((c) => c.state_name === selectedState);
  }, [cities, selectedState]);

  // ---------- Fetch cities ----------
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const cityRes = await publicAuth("cities", "GET");
        const list = cityRes?.data?.data || cityRes?.data?.cities || [];
        setCities(list);
      } catch (err) {
        console.error("Error fetching cities:", err);
        setCities([]);
      }
    };

    if (show) fetchCities();
  }, [show]);

  // ---------- Auto-select current city ----------
  useEffect(() => {
    if (cities.length > 0 && currentCity && !selectedCity) {
      const match = cities.find(
        (c) => c.name?.toLowerCase().trim() === currentCity.toLowerCase().trim()
      );
      if (match) {
        setSelectedState(match.state_name || "");
        setSelectedCity(match);
      }
    }
  }, [cities, currentCity, selectedCity]);

  // OTP resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z\s]+$/.test(name.trim()))
      newErrors.name = "Only alphabets are allowed in name";

    if (!phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(phone.trim()))
      newErrors.phone = "Enter valid 10-digit phone number";

    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Enter a valid email address";

    if (!selectedState) newErrors.state = "State is required";
    if (!selectedCity) newErrors.city = "City is required";

    if (!agree)
      newErrors.agree = "Please agree to the terms before submitting";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const payload = {
        name,
        phone,
        email,
        city_id: selectedCity.id,
      };

      const res = await publicAuth(`register-interest`, "POST", payload);

      if (res.data.status) {
        setSubmissionData(payload);
        setResendTimer(30);
        setCanResend(false);
        setStep("otp");

        // swal({
        //   title: "OTP Sent!",
        //   text: "Please check your email for the OTP verification.",
        //   icon: "success",
        //   timer: 2000,
        //   showConfirmButton: false,
        // });
      } else {
        swal("Error", res.data.message || "Submission failed", "error");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Network error while submitting.";
      swal("Error", msg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return false;
    }

    setIsLoading(true);
    try {
      const res = await publicAuth(`verify-interest-otp`, "POST", {
        email: submissionData.email,
        otp,
      });

      if (res.data.status) {
        await swal({
          title: "Success!",
          text: "Registration successful! Our team will reach out within 3 working days.",
          icon: "success",
          buttons: { confirm: { text: "OK", value: true } },
        });

        resetForm();
        onClose();
        return true;
      } else {
        setOtpError(res.data.message || "Invalid OTP");
        return false;
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || "OTP verification failed");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    setIsLoading(true);
    try {
      await publicAuth(`resend-interest-otp`, "POST", {
        email: submissionData.email,
      });
      setResendTimer(30);
      setCanResend(false);
      setOtp("");
      setOtpError("");
      swal({
        title: "OTP Resent!",
        text: "A new OTP has been sent to your email.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to resend OTP", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToRegister = () => {
    setOtp("");
    setOtpError("");
    setStep("register");
  };

  const resetForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setSelectedState("");
    setSelectedCity(null);
    setAgree(false);
    setErrors({});
    setStep("register");
    setOtp("");
    setOtpError("");
    setResendTimer(0);
    setCanResend(false);
    setSubmissionData(null);
    setIsLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!show || isLoggedIn) return null;

  return (
    <div className="register-popup slide-up bg-primary">
      <div className="container">
        <div className="modal-header border-0 pl-0">
          <h5 className="modal-title text-white">
            {step === "otp" ? "VERIFY YOUR EMAIL" : "REGISTER YOUR INTEREST"}
          </h5>
          <button type="button" className="close text-white" onClick={handleClose}>
            CLOSE ✖
          </button>
        </div>

        {/* Registration Form */}
        {step === "register" && (
          <div className="modal-form">
            <div>
              <input
                type="text"
                placeholder="Name*"
                className="modal-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
              {errors.name && <p className="error-text">{errors.name}</p>}
            </div>

            <div>
              <input
                type="text"
                placeholder="Mobile Number*"
                className="modal-input"
                maxLength={10}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isLoading}
              />
              {errors.phone && <p className="error-text">{errors.phone}</p>}
            </div>

            <div>
              <input
                type="email"
                placeholder="Email*"
                className="modal-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>

            {/* State + City Dropdown */}
            <div>
              <select
                className="modal-input"
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  setSelectedCity(null);
                  setErrors((prev) => ({ ...prev, state: "" }));
                }}
                disabled={isLoading}
              >
                <option value="">Select State*</option>
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {errors.state && <p className="error-text">{errors.state}</p>}
            </div>

            <div>
              <select
                className="modal-input"
                value={selectedCity ? String(selectedCity.id) : ""}
                onChange={(e) => {
                  const city = filteredCities.find((c) => String(c.id) === e.target.value);
                  setSelectedCity(city);
                  setErrors((prev) => ({ ...prev, city: "" }));
                }}
                disabled={isLoading || !selectedState}
              >
                <option value="">Select City*</option>
                {filteredCities.map((city) => (
                  <option key={city.id} value={String(city.id)}>
                    {city.name}
                  </option>
                ))}
              </select>
              {errors.city && <p className="error-text">{errors.city}</p>}
            </div>

            <div>
              <button
                className="modal-btn"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "PROCESSING..." : "ENQUIRE NOW ➔"}
              </button>
            </div>

            <div className="modal-agreement pl-0">
              <div className="checkbox-label">
                <input
                  style={{ height: "16px", width: "22px" }}
                  className="mt-1"
                  type="checkbox"
                  id="agree"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  disabled={isLoading}
                />
                <label htmlFor="agree" className="mb-0 text-white">
                  I agree that by clicking Enquire Now, I am explicitly soliciting a call from Asysta.
                </label>
              </div>
              {errors.agree && <p className="error-text">{errors.agree}</p>}
            </div>
          </div>
        )}

        {/* OTP Verification */}
        {step === "otp" && (
          <div className="modal-form text-center">
            <p className="text-white mb-3">
              Enter the 6-digit OTP sent to <br />
              <strong>{submissionData?.email}</strong>
            </p>

            <div>
              <input
                type="text"
                placeholder="Enter OTP"
                className="modal-input text-center otp-input"
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(val);
                  setOtpError("");
                }}
                maxLength={6}
                disabled={isLoading}
              />
              {otpError && <p className="error-text">{otpError}</p>}
            </div>

            <div className="mb-3">
              <small className="text-white">
                Didn't receive?{" "}
                <span
                  className={`resend-link ${!canResend || isLoading ? "disabled" : ""}`}
                  style={{
                    color: canResend && !isLoading ? "#fff" : "#ccc",
                    textDecoration: canResend && !isLoading ? "underline" : "none",
                    cursor: canResend && !isLoading ? "pointer" : "not-allowed"
                  }}
                  onClick={canResend && !isLoading ? resendOtp : undefined}
                >
                  Resend OTP {resendTimer > 0 ? `in ${resendTimer}s` : ""}
                </span>
              </small>
            </div>

            <div className="d-flex gap-2">
              <button
                className="modal-btn btn-outline flex-1"
                onClick={handleBackToRegister}
                disabled={isLoading}
              >
                BACK
              </button>
              <button
                className="modal-btn flex-1"
                onClick={verifyOtp}
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? "VERIFYING..." : "VERIFY OTP"}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .otp-input {
          letter-spacing: 10px;
          font-size: 1.5rem;
          font-weight: bold;
          height: 50px !important;
        }
        .resend-link {
          color: #fff;
          text-decoration: underline;
          cursor: pointer;
        }
        .resend-link.disabled {
          color: #ccc;
          cursor: not-allowed;
          text-decoration: none;
        }
        .btn-outline {
          background: transparent;
          border: 2px solid #fff;
          color: #fff;
        }
        .btn-outline:hover {
          background: rgba(255,255,255,0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default RegisterInterestModal;
